import clsx from 'clsx'
import type {
  MouseEventHandler,
  ComponentProps,
} from 'react'
import React, {
  useCallback,
  useRef,
} from 'react'

import type { EllipsisMenuProps } from './common'
import ItemCardModalMenu from './MenuModal'

import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import useToggle from 'hooks/useToggle'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import AssistChip from 'LuxKit/Chips/AssistChip'

interface Props extends EllipsisMenuProps {
  className?: string
  size?: ComponentProps<typeof AssistChip>['size']
  variant?: ComponentProps<typeof AssistChip>['variant']
  modalOnMobile?: boolean
}

export default function MenuButton({
  className,
  title,
  subtitle,
  bodyContent,
  footerContent,
  size = 'small',
  variant = 'outline',
  modalOnMobile = false,
}: Props) {
  const openModal = useOpenModal()

  const isMobileScreen = useScreenSizeOnly('mobile')

  const {
    value: dropdownOpen,
    toggle: toggleDropdown,
    off: closeDropdown,
  } = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)

  const toggleMenu: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (isMobileScreen && modalOnMobile) {
        openModal(
          ItemCardModalMenu,
          { bodyContent, footerContent, title, subtitle },
          { height: 'auto' },
        )
      } else {
        toggleDropdown()
      }
    },
    [
      isMobileScreen,
      modalOnMobile,
      openModal,
      bodyContent,
      footerContent,
      toggleDropdown,
      title,
      subtitle,
    ],
  )

  const onContentClick: MouseEventHandler<HTMLDivElement> = useCallback(e => {
    // If user clicked a button or a link within the dropdown, close the dropdown
    if (e.target instanceof HTMLElement) {
      const linkOrButton = e.target.closest('button, a')
      if (e.currentTarget.contains(linkOrButton)) {
        closeDropdown()
      }
    }
  }, [closeDropdown])

  return (
    <>
      <AssistChip
        variant={variant}
        size={size}
        data-testid="action-menu-button"
        aria-label="action menu"
        onClick={toggleMenu}
        className={clsx(className, 'no-print')}
        ref={triggerRef}
        startIcon={<LineEllipsisVIcon />}
      />
      <DropdownList
        size="S"
        triggerRef={triggerRef}
        anchorRef={triggerRef}
        open={dropdownOpen}
        onClose={closeDropdown}
        placement="bottom-end"
        portaled
      >
        <div onClick={onContentClick}>
          {bodyContent}
          {footerContent}
        </div>
      </DropdownList>
    </>
  )
}
